import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router';

import { theme } from '../../../../theme/theme';

import type { ReactNode } from 'react';

export enum ApplicationsMenuItemSize {
	Regular = 'Regular',
	Small = 'Small',
}

export enum ApplicationsMenuItemLinkType {
	Internal = 'Internal',
	External = 'External',
}

export interface ApplicationsMenuItem {
	readonly id: string;
	readonly icon: ReactNode;
	readonly title: string;
	readonly description?: string;
	readonly link: string;
	readonly isSelected?: boolean;
	readonly size?: ApplicationsMenuItemSize;
	readonly linkType?: ApplicationsMenuItemLinkType;
	readonly onClick?: () => void;
}

const SELECTED_ATTR = 'data-selected';

const TextContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

const ItemTitle = styled.span`
	font-weight: ${theme.font.weights.bold};
	color: ${theme.font.colors.base};
`;

const ItemDescription = styled.span`
	font-size: ${theme.font.sizes.sm};
	color: ${theme.font.colors.secondary};
`;

const REGULAR_SELECTION_INDICATOR_SIZE = 70;
const REGULAR_SELECTION_INDICATOR_RADIUS = theme.border.radii.base;
const REGULAR_SELECTION_INDICATOR_THICKNESS = 3;

const RegularSelectionIndicator = styled.div`
	max-inline-size: ${REGULAR_SELECTION_INDICATOR_THICKNESS}px;
	min-inline-size: ${REGULAR_SELECTION_INDICATOR_THICKNESS}px;
	block-size: ${REGULAR_SELECTION_INDICATOR_SIZE}px;
	border-start-end-radius: ${REGULAR_SELECTION_INDICATOR_RADIUS}px;
	border-end-end-radius: ${REGULAR_SELECTION_INDICATOR_RADIUS}px;
	margin-inline-end: ${theme.margins.xs};
	background-color: transparent;

	[${SELECTED_ATTR}] & {
		background-color: ${theme.colors.purple6};
	}
`;

const RegularIconContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	min-inline-size: 70px;
	max-inline-size: 70px;
	min-block-size: 70px;
	max-block-size: 70px;
	margin-inline-end: 14px;
	background-color: ${theme.colors.colorFillSecondary};
	border: none;

	[${SELECTED_ATTR}] & {
		background-color: ${theme.colors.purple1};
		border: 1px solid ${theme.colors.purple2};
	}
`;

const RegularHoverContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: start;
	margin-block: ${theme.margins.xs};
	margin-inline: 0 ${theme.margins.base};
	border-radius: ${theme.border.radii.base};
	padding: ${theme.paddings.xs};
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: auto;
`;

const REGULAR_CONTAINER_STYLE = css`
	display: flex;
	align-items: center;
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: auto;

	&:hover {
		${RegularHoverContainer} {
			background-color: ${theme.colors.controlItemBgHover};
		}
	}
`;

const RegularLinkContainer = styled(Link)`
	${REGULAR_CONTAINER_STYLE};
`;

const RegularAHrefContainer = styled.a`
	${REGULAR_CONTAINER_STYLE};
`;

const SmallSelectionIndicator = styled.div`
	max-inline-size: 3px;
	min-inline-size: 3px;
	block-size: 46px;
	border-end-end-radius: 5px;
	margin-inline-end: 5px;
	background-color: transparent;

	[${SELECTED_ATTR}] & {
		background-color: ${theme.colors.purple6};
	}
`;

const SmallIconContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	margin-inline-end: 14px;
	background-color: ${theme.colors.colorFillSecondary};
	border: none;

	[${SELECTED_ATTR}] & {
		background-color: ${theme.colors.purple1};
		border: 1px solid ${theme.colors.purple2};
	}
`;

const SmallHoverContainer = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: ${theme.border.radii.base};
	padding: ${theme.paddings.xs};
`;

const SMALL_CONTAINER_STYLE = css`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	border-block-start: 1px solid ${theme.border.colors.base};

	&:hover {
		background-color: ${theme.colors.controlItemBgHover};
	}
`;

const SmallAHrefContainer = styled.a`
	${SMALL_CONTAINER_STYLE};
`;

const SmallLinkContainer = styled(Link)`
	${SMALL_CONTAINER_STYLE};
`;

export const MainLayoutApplicationsMenuItem = observer(function MainLayoutApplicationsMenuItem({
	icon,
	title,
	description,
	link,
	isSelected = false,
	size = ApplicationsMenuItemSize.Regular,
	linkType = ApplicationsMenuItemLinkType.Internal,
	onClick,
}: ApplicationsMenuItem) {
	const selectedAttr = isSelected ? { [SELECTED_ATTR]: '' } : {};

	if (size === ApplicationsMenuItemSize.Small) {
		const smallContent = (
			<>
				<SmallSelectionIndicator />
				<SmallHoverContainer>
					<SmallIconContainer>{icon}</SmallIconContainer>
					<TextContainer>
						<ItemTitle>{title}</ItemTitle>
					</TextContainer>
				</SmallHoverContainer>
			</>
		);

		if (linkType === ApplicationsMenuItemLinkType.Internal) {
			return (
				<SmallLinkContainer to={link} onClick={onClick} {...selectedAttr}>
					{smallContent}
				</SmallLinkContainer>
			);
		}

		return (
			<SmallAHrefContainer href={link} onClick={onClick} {...selectedAttr}>
				{smallContent}
			</SmallAHrefContainer>
		);
	}

	const regularContent = (
		<>
			<RegularSelectionIndicator />
			<RegularHoverContainer>
				<RegularIconContainer>{icon}</RegularIconContainer>
				<TextContainer>
					<ItemTitle>{title}</ItemTitle>
					{description ? <ItemDescription>{description}</ItemDescription> : null}
				</TextContainer>
			</RegularHoverContainer>
		</>
	);

	if (linkType === ApplicationsMenuItemLinkType.External) {
		return (
			<RegularAHrefContainer href={link} onClick={onClick} {...selectedAttr}>
				{regularContent}
			</RegularAHrefContainer>
		);
	}

	return (
		<RegularLinkContainer to={link} onClick={onClick} {...selectedAttr}>
			{regularContent}
		</RegularLinkContainer>
	);
});
